// Blocco «Fatturazione» condiviso (item 17).
//
// Gli stessi dati servono in DUE posti — pagina ordine e contesto Messaggi —
// e vivono in un solo componente apposta: due copie divergono al primo campo
// aggiunto. In `shared/ui` perché i consumatori stanno in feature diverse
// (ordini e messaggi).

import type { ReactNode } from "react";
import { formatDateIt } from "@/shared/format";

/** DTO di passaggio: snake_case come arriva dal backend, non rimappato. */
export interface InvoiceSummary {
  doc_type?: string | null;
  document_number?: string | null;
  document_date?: string | null;
  invoice_date?: string | null;
  status?: string | null;
  eureka_check?: string | null;
}

// «da_inviare» non dice niente a chi legge: le etichette parlano.
// ⛔ «Trasmessa» = lo stato che OM conosce (status), NON un FAD verificato
// sullo SdI: quel segnale OM non lo persiste e non va inventato qui.
const STATUS_LABELS: Record<string, string> = {
  da_fatturare: "Da fatturare",
  in_revisione: "In revisione",
  da_inviare: "Emessa, da trasmettere",
  fatturata: "Trasmessa",
  esclusa: "Esclusa",
  in_attesa_spedizione: "In attesa di spedizione",
  in_attesa_pagamento: "In attesa di pagamento",
};

const DOC_TYPE_LABELS: Record<string, string> = {
  SF: "SF · corrispettivo",
  FA: "FA · fattura elettronica",
  OSS: "OSS · vendita a distanza UE",
  ST: "ST · nota di credito",
  RC: "RC · nota di credito",
  RCO: "RCO · nota di credito",
};

// Formato condiviso (shared/format): il default della locale varia per runtime
// (21/7 vs 21/07). Vuoto → null (la riga non si stampa), non parsabile → il
// valore grezzo: fail-soft, il dato non si perde.
const formatDate = (iso?: string | null): string | null => formatDateIt(iso) || null;

/** `className`: il colore d'area lo decide il CHIAMANTE. Il blocco vive in due
 *  posti (pagina ordine, contesto Messaggi) e solo uno dei due ha una colonna
 *  «soldi» da cui ereditare il verde: cablarlo qui lo tingerebbe anche dove non
 *  significa niente. */
export function InvoiceInfo({
  invoice,
  className = "",
  azione,
}: {
  invoice?: InvoiceSummary | null;
  className?: string;
  /** Tasto opzionale in fondo al blocco (pagina ordine: «Vendita extra»). */
  azione?: ReactNode;
}) {
  if (!invoice) return null;

  const rows: Array<[string, string]> = [];
  if (invoice.doc_type) {
    rows.push(["Tipo", DOC_TYPE_LABELS[invoice.doc_type] ?? invoice.doc_type]);
  }
  if (invoice.document_number) rows.push(["Numero", invoice.document_number]);
  const docDate = formatDate(invoice.document_date);
  if (docDate) rows.push(["Data documento", docDate]);
  const invDate = formatDate(invoice.invoice_date);
  if (invDate) rows.push(["Data fattura", invDate]);
  if (invoice.status) rows.push(["Stato", STATUS_LABELS[invoice.status] ?? invoice.status]);
  if (invoice.eureka_check) rows.push(["Eureka", invoice.eureka_check]);

  // Emissione presente ma senza un solo campo leggibile: meglio niente che una
  // scatola vuota che sembra un errore.
  if (rows.length === 0) return null;

  return (
    <section
      data-testid="invoice-info"
      className={`mb-4 rounded-xl border border-line bg-card px-4 py-3.5 shadow-[0_1px_2px_rgba(31,27,22,0.04)] ${className}`.trimEnd()}
    >
      <h3 className="mb-2 text-[15px] font-bold leading-tight text-ink">Fatturazione</h3>
      <dl className="flex flex-col gap-1">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-baseline justify-between gap-3">
            <dt className="text-[12px] font-semibold text-ink">{label}</dt>
            <dd className="text-right font-mono text-[12.5px] tabular-nums text-ink">{value}</dd>
          </div>
        ))}
      </dl>
      {azione && <div className="mt-3">{azione}</div>}
    </section>
  );
}
